import { Plus, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatAmount } from "@/lib/format"
import { BILLING_MODEL_LABELS } from "@/lib/types"
import type { BillingModel, ContractLine } from "@/lib/types"
import { t } from "@/lib/i18n"

/**
 * Frecuencias que el backend calendariza. La lista vive aca porque es una
 * decision de la SPA sobre que ofrecer: la columna es texto libre.
 */
const FRECUENCIAS = ["mensual", "trimestral", "anual", "hito", "por consumo"]

/** Una linea nueva no arranca vacia: arranca en el caso mas comun. */
export function lineaNueva(): ContractLine {
  return { description: "", billingModel: "fixed_recurring", amount: 0,
           quantity: 1, frequency: "mensual" }
}

/**
 * Por que no se puede guardar, o `null`.
 *
 * Las mismas reglas que el backend rechaza con 422, comprobadas ANTES de mandar:
 * un 422 llega despues de teclear el formulario entero y nombra un campo con el
 * nombre que tiene en la base, no el que se lee en pantalla.
 */
export function problemaDeLineas(lines: ContractLine[]): string | null {
  if (lines.length === 0) {
    return "Un contrato necesita al menos una línea: es de las líneas —no de la carátula— de donde salen las ventas proyectadas."
  }
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    if (!(l.amount > 0)) return `Línea ${i + 1}: el importe tiene que ser mayor que cero.`
    if (!(l.quantity > 0)) return `Línea ${i + 1}: la cantidad tiene que ser mayor que cero.`
    if (l.billingModel === "fixed_recurring" && !l.frequency?.trim()) {
      return `Línea ${i + 1}: una línea recurrente necesita frecuencia; sin ella no se puede proyectar.`
    }
    if (l.startDate && l.endDate && l.endDate < l.startDate) {
      return `Línea ${i + 1}: termina antes de empezar.`
    }
  }
  return null
}

/**
 * Las lineas de un contrato.
 *
 * DE AQUI SALE EL INGRESO PROYECTADO, no de la caratula, y por eso el backend
 * rechaza un contrato sin ninguna. Durante un tiempo la SPA escribia una linea
 * unica y leia la primera: un contrato con dos hitos de 80k y 100k se mostraba
 * como 80.000 —el valor comprometido eran 180.000— y editar cualquier campo de
 * la caratula borraba la segunda linea, porque un PATCH con `lineas` las
 * reemplaza todas. El dato estaba bien en la base y mal en la pantalla, que es
 * la forma de equivocarse mas dificil de notar.
 *
 * La FRECUENCIA solo se pide donde significa algo. En una linea por hitos no hay
 * nada que calendarizar, y un desplegable habilitado con «mensual» dentro invita
 * a dejarlo puesto y a que alguien lo lea despues como si fuera un dato.
 */
export function ContractLinesEditor({
  lines, onChange, currency,
}: {
  lines: ContractLine[]
  onChange: (lines: ContractLine[]) => void
  currency: string
}) {
  const total = lines.reduce((a, l) => a + (l.amount || 0) * (l.quantity || 0), 0)

  function actualizar(i: number, cambio: Partial<ContractLine>) {
    onChange(lines.map((l, j) => (j === i ? { ...l, ...cambio } : l)))
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>
          {t("Líneas del contrato")}{" "}<span className="text-destructive">*</span>
        </Label>
        <Button size="xs" variant="outline" onClick={() => onChange([...lines, lineaNueva()])}>
          <Plus />
          {t("Añadir línea")}</Button>
      </div>

      <div className="space-y-1.5 rounded-md border bg-surface-sunken/50 p-2">
        {lines.length === 0 ? (
          <p className="py-2 text-center text-2xs text-muted-foreground">
            {t("Sin líneas, este contrato no proyecta ningún ingreso.")}</p>
        ) : null}

        {lines.map((l, i) => (
          <div key={l.id ?? `nueva-${i}`} className="grid grid-cols-12 items-end gap-1.5">
            <div className="col-span-4 space-y-0.5">
              {i === 0 ? <span className="eyebrow">{t("Descripción")}</span> : null}
              <Input
                aria-label={`Descripción de la línea ${i + 1}`}
                value={l.description}
                placeholder={t("Qué se factura")}
                onChange={(e) => actualizar(i, { description: e.target.value })}
              />
            </div>

            <div className="col-span-3 space-y-0.5">
              {i === 0 ? <span className="eyebrow">{t("Modelo")}</span> : null}
              <Select
                value={l.billingModel}
                onValueChange={(v) => {
                  const modelo = v as BillingModel
                  actualizar(i, {
                    billingModel: modelo,
                    // Cambiar a un modelo que no calendariza limpia la
                    // frecuencia: dejarla puesta la convierte en un dato falso.
                    frequency: modelo === "fixed_recurring"
                      ? (l.frequency || "mensual")
                      : undefined,
                  })
                }}
              >
                <SelectTrigger aria-label={`Modelo de la línea ${i + 1}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(BILLING_MODEL_LABELS) as BillingModel[]).map((k) => (
                    <SelectItem key={k} value={k}>{BILLING_MODEL_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 space-y-0.5">
              {i === 0 ? <span className="eyebrow">{t("Importe")}</span> : null}
              <Input
                aria-label={`Importe de la línea ${i + 1}`}
                type="number" step="0.01" className="num"
                value={l.amount === 0 ? "" : String(l.amount)}
                onChange={(e) => actualizar(i, { amount: Number(e.target.value) || 0 })}
              />
            </div>

            <div className="col-span-1 space-y-0.5">
              {i === 0 ? <span className="eyebrow">{t("Cant.")}</span> : null}
              <Input
                aria-label={`Cantidad de la línea ${i + 1}`}
                type="number" min="1" className="num"
                value={String(l.quantity)}
                onChange={(e) => actualizar(i, { quantity: Number(e.target.value) || 0 })}
              />
            </div>

            <div className="col-span-1 space-y-0.5">
              {i === 0 ? <span className="eyebrow">{t("Frec.")}</span> : null}
              {l.billingModel === "fixed_recurring" ? (
                <Select
                  value={l.frequency ?? "mensual"}
                  onValueChange={(v) => actualizar(i, { frequency: v })}
                >
                  <SelectTrigger aria-label={`Frecuencia de la línea ${i + 1}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FRECUENCIAS.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="block py-1.5 text-center text-2xs text-muted-foreground">—</span>
              )}
            </div>

            <div className="col-span-1 flex justify-end">
              <Button
                size="xs" variant="ghost"
                aria-label={`Quitar la línea ${i + 1}`}
                onClick={() => onChange(lines.filter((_, j) => j !== i))}
              >
                <Trash2 />
              </Button>
            </div>
          </div>
        ))}

        {lines.length > 0 ? (
          <div className="flex items-baseline justify-end gap-2 border-t pt-1.5 text-xs">
            <span className="eyebrow">{t("Valor comprometido")}</span>
            <span className="num font-semibold">{formatAmount(total)}</span>
            <span className="text-2xs text-muted-foreground">{currency}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
